import React, { useState, useEffect, useCallback } from 'react';
import { View, FlatList, StyleSheet, RefreshControl, TouchableOpacity, Alert, Modal } from 'react-native';
import { Text, Card, FAB, Button, useTheme, ActivityIndicator, Divider, Snackbar, IconButton, Chip, TextInput } from 'react-native-paper';
import { useFocusEffect } from '@react-navigation/native';
import { getAllCategories, createCategory, updateCategory, deleteCategory } from '../../../api/adminAPI';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import EmptyState from '../../../components/EmptyState';

const AdminMenuScreen = ({ navigation }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [error, setError] = useState(null);
  
  const [categories, setCategories] = useState([]);
  
  // Dialog state
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  // Verileri yükle
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Kategorileri yükle
      try {
        const categoriesResponse = await getAllCategories();
        if (categoriesResponse.success) {
          setCategories(categoriesResponse.data || []);
        } else {
          console.warn('Categories could not be loaded:', categoriesResponse.message);
          setSnackbarMessage(categoriesResponse.message || 'Kategoriler yüklenemedi');
          setSnackbarVisible(true);
        }
      } catch (categoryError) {
        console.error('Error fetching categories:', categoryError);
        setSnackbarMessage('Kategoriler yüklenirken bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
        setSnackbarVisible(true);
      }
    } catch (error) {
      console.error('General error fetching menu data:', error);
      setError(error.message || 'Menü verileri yüklenemedi');
      setSnackbarMessage(error.message || 'Menü verileri yüklenemedi. Lütfen daha sonra tekrar deneyin.');
      setSnackbarVisible(true);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  // İlk yükleme
  useEffect(() => {
    fetchData();
  }, []);
  
  // Ekrana her dönüldüğünde yenile
  useFocusEffect(
    useCallback(() => {
      fetchData();
    }, [])
  );
  
  // Kategori düzenleme sayfasına git
  const handleEditCategory = (category) => {
    //console.log('Edit button clicked for category:', category.name);
    navigation.navigate('CategoryForm', { category });
  };
  
  // Kategori silme işlemi
  const handleDeleteCategory = (category) => {
    //console.log('Delete button clicked for category:', category.name);
    setCategoryToDelete(category);
    setDeleteDialogVisible(true);
  };

  // Kategori silme onayı
  const confirmDeleteCategory = async () => {
    if (!categoryToDelete) return;
    
    //console.log('Delete confirmation button clicked');
    try {
      //console.log(`Deleting category ${categoryToDelete.id}: ${categoryToDelete.name}`);
      
      const response = await deleteCategory(categoryToDelete.id);
      //console.log('Delete category response:', JSON.stringify(response));
      
      if (response.success) {
        setCategories(categories.filter(c => c.id !== categoryToDelete.id));
        setSnackbarMessage('Kategori başarıyla silindi');
        setSnackbarVisible(true);
      } else {
        console.error('Delete category server error:', response.message);
        setSnackbarMessage(response.message || 'Kategori silinirken bir hata oluştu');
        setSnackbarVisible(true);
      }
    } catch (error) {
      console.error('Delete category client error:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
        console.error('Error status:', error.response.status);
      }
      setSnackbarMessage('Kategori silinirken bir hata oluştu');
      setSnackbarVisible(true);
    } finally {
      setDeleteDialogVisible(false);
      setCategoryToDelete(null);
    }
  };

  // Dialog'u kapat
  const hideDeleteDialog = () => {
    setDeleteDialogVisible(false);
    setCategoryToDelete(null);
  };
  
  // Kategori ürünlerini görüntülemeye git
  const handleViewCategoryItems = (category) => {
    //console.log('View items button clicked for category:', category.name);
    navigation.navigate('MenuItems', { 
      categoryId: category.id, 
      categoryName: category.name 
    });
  };
  
  // Yeni kategori ekleme sayfasına git
  const handleAddCategory = () => {
    navigation.navigate('CategoryForm');
  };
  
  const renderCategoryItem = ({ item }) => (
    <Card style={styles.categoryCard} onPress={() => handleViewCategoryItems(item)}>
      <Card.Content>
        <View style={styles.categoryHeader}>
          <View>
            <Text style={styles.categoryName}>{item.name}</Text>
            {item.description && (
              <Text style={styles.categoryDescription} numberOfLines={2}>
                {item.description}
              </Text>
            )}
            {/*
            <Chip 
              icon={item.is_active ? "check-circle" : "close-circle"} 
              style={[styles.statusChip, {backgroundColor: item.is_active ? 'rgba(125, 76, 236, 0.15)' : 'rgba(125, 76, 236, 0.05)', borderColor: 'rgb(125, 76, 236)'}]}
              textStyle={{color: 'rgb(125, 76, 236)', fontWeight: 'bold'}}
              mode="outlined"
            >
              {item.is_active ? 'Aktif' : 'Pasif'}
            </Chip>
            */}
          </View>
          
          <View style={styles.categoryActions}>
            <IconButton
              icon="silverware-fork-knife"
              size={24}
              onPress={() => handleViewCategoryItems(item)}
              color="rgb(125, 76, 236)"
            />
            <IconButton
              icon="pencil"
              size={24}
              onPress={() => handleEditCategory(item)}
              color="rgb(125, 76, 236)"
            />
            <IconButton
              icon="delete"
              size={24}
              onPress={() => {
                //console.log(`Delete icon clicked for: ${item.name}`);
                handleDeleteCategory(item);
              }}
              color={theme.colors.error}
            />
          </View>
        </View>
      </Card.Content>
    </Card>
  );
  
  const onRefresh = React.useCallback(() => {
    setRefreshing(true);
    fetchData();
  }, []);
  
  if (loading && !refreshing) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="rgb(125, 76, 236)" />
        <Text style={styles.loadingText}>Menü yükleniyor...</Text>
      </View>
    );
  }
  
  return (
    <View style={styles.container}>
      {categories.length === 0 ? (
        <EmptyState
          icon="food-off"
          title="Henüz kategori yok"
          description="Menü oluşturabilmek için önce bir kategori ekleyin"
          buttonText="Kategori Ekle"
          onButtonPress={handleAddCategory}
        />
      ) : (
        <FlatList
          data={categories}
          keyExtractor={item => item.id.toString()}
          renderItem={renderCategoryItem}
          contentContainerStyle={styles.listContent}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['rgb(125, 76, 236)']} />}
        />
      )}
      
      <FAB
        style={styles.fab}
        icon="plus"
        onPress={handleAddCategory}
        color="white"
      />
      
      {/* Silme Onay Modal */}
      <Modal
        visible={deleteDialogVisible}
        transparent={true}
        onRequestClose={hideDeleteDialog}
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <MaterialCommunityIcons 
              name="delete-alert-outline" 
              size={50} 
              color="rgb(125, 76, 236)"
              style={[styles.modalIcon, {backgroundColor: 'rgba(125, 76, 236, 0.1)', borderRadius: 50, padding: 10}]} 
            />
            
            <Text style={styles.modalTitle}>Kategori Sil</Text>
            <Text style={styles.modalText}>
              {categoryToDelete 
                ? `"${categoryToDelete.name}" kategorisini silmek istediğinize emin misiniz?` 
                : 'Bu kategoriyi silmek istediğinize emin misiniz?'}
            </Text>
            
            <View style={styles.modalButtonContainer}>
              <Button 
                mode="contained" 
                onPress={confirmDeleteCategory}
                style={[styles.modalButton, {backgroundColor: 'rgb(125, 76, 236)'}]}
                icon="delete"
                labelStyle={{color: 'white'}}
              >
                <Text style={{color: 'white', fontWeight: 'bold'}}>Sil</Text>
              </Button>
              <Button 
                mode="outlined" 
                onPress={hideDeleteDialog}
                style={[styles.modalButton, {borderColor: 'rgb(125, 76, 236)'}]}
                textColor="rgb(125, 76, 236)"
              >
                İptal
              </Button>
            </View>
          </View>
        </View>
      </Modal>
      
      <Snackbar
        visible={snackbarVisible}
        onDismiss={() => setSnackbarVisible(false)}
        duration={3000}
        style={{backgroundColor: 'rgb(125, 76, 236)'}}
        theme={{colors: {surface: '#fff'}}}
        action={{
          label: 'Tamam',
          onPress: () => setSnackbarVisible(false),
          color: '#fff'
        }}>
        {snackbarMessage}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'rgb(232, 162, 249)',
  },
  modalContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  modalContent: {
    width: '80%',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: 10,
    padding: 20,
    alignItems: 'center',
    elevation: 5,
    borderWidth: 1,
    borderColor: 'rgb(125, 76, 236)',
  },
  modalIcon: {
    marginBottom: 15,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 10,
    color: 'rgb(125, 76, 236)',
  },
  modalText: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 20,
  },
  modalButtonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    width: '100%',
  },
  modalButton: {
    marginHorizontal: 5,
    minWidth: 120,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
  },
  listContent: {
    padding: 16,
  },
  categoryCard: {
    marginBottom: 12,
    elevation: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
  },
  categoryHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  categoryName: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 4,
    color: 'rgb(125, 76, 236)',
  },
  categoryDescription: {
    fontSize: 14,
    color: '#757575',
    marginBottom: 8,
  },
  statusChip: {
    alignSelf: 'flex-start',
    marginTop: 4,
  },
  categoryActions: {
    flexDirection: 'row',
  },
  fab: {
    position: 'absolute',
    margin: 16,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgb(125, 76, 236)',
  },
});

export default AdminMenuScreen;
